<template>
  <div class="flex flex-col glass-frosted rounded-8xl p-4 relative box-border">
    <div class="flex items-center mb-4 font-bold">
      <div class="flex items-center mr-1 text-l-medium text-black-white">
        <Icon icon="LightEffect-Wireframe-DecorLight" class="size-6" />
        {{ $t('lighting.lightingDirection') }}
      </div>
      <div class="flex-1"></div>
      <Button
        v-for="(item, index) in switchList"
        :key="index"
        size="xs"
        color="glass"
        class="flex items-center mr-2 px-2"
        shape="corner"
        @click="item.fn"
      >
        <Icon :icon="item.icon" class="size-3" />
        {{ item.label }}
      </Button>
    </div>
    <div class="flex items-center w-full">
      <div
        class="min-w-0 h-10 rounded-full flex-1 w-full"
        :style="{
          background: colorIllustration,
        }"
      ></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { type LightingEffectDefinition, type RGB, type RGBGroup } from '@rapoo/device-sdk'
  import { Icon, Button } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'

  import { computed } from 'vue'

  const props = defineProps<{
    modelValue: RGBGroup
    gradientDirection: number
    /** 当前编辑区灯效；由页面 props 下传 */
    currentEffectInfo?: Pick<LightingEffectDefinition, 'i18nKey'> | null
  }>()

  const emit = defineEmits(['update:modelValue', 'change'])

  const { t } = useLocaleStore()

  const onlyRow = computed(() => props.currentEffectInfo?.i18nKey?.includes('row_gradient'))
  const onlyCol = computed(() => props.currentEffectInfo?.i18nKey?.includes('col_gradient'))

  const switchList = computed(() => {
    const list = [
      {
        label: t('lighting.verticalGradient'),
        icon: 'LightEffect-Wireframe-BacklightSwitch',
        type: 'col',
        fn: () => {
          const rgbStart = props.modelValue[0] as RGB
          const rgbEnd = props.modelValue[1] as RGB
          const rgbGroup = [rgbEnd, rgbStart]
          emit('update:modelValue', rgbGroup)
          emit('change', {
            rgbGroup,
            direction: 2,
          })
        },
      },
      {
        label: t('lighting.horizontalGradient'),
        icon: 'LightEffect-Wireframe-SideLightSwitch',
        type: 'row',
        fn: () => {
          const rgbStart = props.modelValue[0] as RGB
          const rgbEnd = props.modelValue[1] as RGB
          const rgbGroup = [rgbEnd, rgbStart]
          emit('update:modelValue', rgbGroup)
          emit('change', {
            rgbGroup,
            direction: 3,
          })
        },
      },
    ]
    if (onlyRow.value) {
      return list.filter((item) => item.type === 'row')
    }
    if (onlyCol.value) {
      return list.filter((item) => item.type === 'col')
    }
    return list
  })

  const colorIllustration = computed(() => {
    const [c1, c2] = props.modelValue
    const dir = props.gradientDirection === 3 ? 'right' : 'bottom'
    return `linear-gradient(to ${dir}, rgb(${c1.r},${c1.g},${c1.b}), rgb(${c2.r},${c2.g},${c2.b}))`
  })
</script>
